import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { inr, timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Download, TrendingUp, ArrowRight } from "lucide-react";

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => { setRes(r.data); setOpen(true); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const hasResults = res && (res.riders.length || res.vehicles.length || res.batteries.length || res.invoices.length);

  const Group = ({ title, items, render }) => items.length > 0 && (
    <div>
      <div className="px-3 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{title}</div>
      {items.map(render)}
    </div>
  );
  const Row = ({ onClick, testid, children }) => (
    <button onClick={onClick} data-testid={testid}
      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between text-slate-700">{children}</button>
  );

  return (
    <div className="relative w-full max-w-sm" ref={boxRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)}
        onFocus={() => res && setOpen(true)}
        placeholder="Search rider, vehicle, battery, invoice…"
        className="pl-9 bg-white border-slate-200 h-9 rounded-full shadow-sm" />
      {open && res && (
        <div className="absolute z-30 mt-1 w-full card-tactical shadow-xl max-h-96 overflow-y-auto" data-testid="global-search-results">
          {!hasResults && <div className="p-4 text-sm text-slate-500">No matches for “{q}”.</div>}
          <Group title="Riders" items={res.riders} render={(r) => (
            <Row key={r.rider_id} onClick={() => go(`/riders/${r.id}`)} testid={`search-rider-${r.rider_id}`}>
              <span>{r.name} <span className="text-slate-400 font-mono text-xs">{r.rider_id}</span></span>
              <span className="font-mono text-xs text-slate-400">{r.mobile}</span>
            </Row>
          )} />
          <Group title="Vehicles" items={res.vehicles} render={(v) => (
            <Row key={v.vehicle_id} onClick={() => go("/vehicles")} testid={`search-vehicle-${v.vehicle_id}`}>
              <span className="font-mono">{v.vehicle_id}</span>
              <span className="font-mono text-xs text-slate-400">{v.registration_number || v.chassis_last4}</span>
            </Row>
          )} />
          <Group title="Batteries" items={res.batteries} render={(b) => (
            <Row key={b.battery_id} onClick={() => go("/batteries")} testid={`search-battery-${b.battery_id}`}>
              <span className="font-mono">{b.battery_id}</span>
              <span className="font-mono text-xs text-slate-400">{b.serial_number}</span>
            </Row>
          )} />
          <Group title="Invoices" items={res.invoices} render={(i) => (
            <Row key={i.invoice_id} onClick={() => go("/invoices")} testid={`search-invoice-${i.invoice_id}`}>
              <span className="font-mono">{i.invoice_id}</span>
              <span className="font-mono text-xs text-slate-400">{i.rider_name} · {inr(i.amount)}</span>
            </Row>
          )} />
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, subTone = "text-emerald-600", to, testid, icon: Icon }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => to && navigate(to)} data-testid={testid}
      className="card-tactical p-5 text-left cursor-pointer hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 m-0">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-slate-600" />}
      </div>
      <p className="text-[34px] leading-tight font-extrabold font-heading m-0 mt-1">{value}</p>
      {sub && <p className={`text-xs font-medium mt-1 mb-0 ${subTone}`}>{sub}</p>}
    </button>
  );
}

function SwapsChart({ byHour, peakHour }) {
  const hours = [...Array(24).keys()].map((h) => (h + 6) % 24); // 06..23,00..05
  const max = Math.max(...byHour, 1);
  const curHour = new Date().getHours();
  return (
    <div className="card-tactical p-5 lg:col-span-2" data-testid="swaps-chart">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-[15px] m-0">Swaps by hour</h3>
        {peakHour !== null && peakHour !== undefined && (
          <span className="text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-2.5 py-1" data-testid="peak-chip">
            Peak {String(peakHour).padStart(2, "0")}:00
          </span>
        )}
      </div>
      {max === 1 && byHour.every((v) => v === 0) ? (
        <p className="text-sm text-slate-400 py-8 text-center">No swaps recorded today yet.</p>
      ) : (
        <div className="flex items-end gap-1.5 h-40">
          {hours.map((h) => (
            <div key={h} className="flex-1 h-full flex flex-col items-center justify-end gap-1.5 min-w-0">
              <div
                className={`w-full rounded-t-md transition-all duration-500 ${
                  h === peakHour ? "bg-teal-600" : h === curHour ? "bg-sky-400" : "bg-teal-400/70"}`}
                style={{ height: `${Math.max((byHour[h] / max) * 100, 3)}%` }}
                title={`${String(h).padStart(2, "0")}:00 — ${byHour[h]} swaps`} />
              {h % 3 === 0 && <span className="text-[10px] text-slate-400 font-mono">{String(h).padStart(2, "0")}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(new Date());

  const load = useCallback(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  useEffect(() => {
    load();
    const dataTimer = setInterval(load, 60000);
    const clock = setInterval(() => setNow(new Date()), 30000);
    return () => { clearInterval(dataTimer); clearInterval(clock); };
  }, [load]);

  if (!data) return <div className="text-slate-400">Loading dashboard…</div>;
  const k = data.kpis;
  const faulty = data.batteries["Faulty"];
  const onHold = data.batteries["On Hold"];
  const dateLine = now.toLocaleDateString("en-IN", { weekday: "long", timeZone: "Asia/Kolkata" }).toUpperCase()
    + ", " + now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) + " · IST";
  const totalRidersZones = data.zones.reduce((a, z) => a + z.count, 0);

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold tracking-[0.18em] text-teal-700 mb-1" data-testid="hero-date">{dateLine}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight m-0">Network Overview</h1>
        </div>
        <div className="flex items-center gap-3">
          <GlobalSearch />
          <span className="text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 shadow-sm" data-testid="range-chip">Last 24h</span>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/export`}>
            <Button data-testid="export-report-btn"
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full shadow-sm">
              <Download className="h-4 w-4 mr-1.5" /> Export report
            </Button>
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-row">
        <KpiCard label="Swaps today" value={k.swaps_range}
          sub={`${k.swaps_delta >= 0 ? "▲" : "▼"} ${Math.abs(k.swaps_delta)}% vs yesterday`}
          subTone={k.swaps_delta >= 0 ? "text-emerald-600" : "text-red-500"}
          to="/swap-history" testid="kpi-swaps-today" icon={TrendingUp} />
        <KpiCard label="Active riders" value={data.active_riders}
          sub={`${data.zones.length} zone${data.zones.length === 1 ? "" : "s"} on network`}
          to="/riders?status=Active" testid="kpi-active-riders" />
        <KpiCard label="Batteries ready" value={data.batteries["Available"]}
          sub={`${onHold} on hold`} subTone="text-amber-600"
          to="/batteries?status=Available" testid="kpi-batteries-ready" />
        <KpiCard label="Fault alerts" value={faulty + onHold + data.blocked_riders.length}
          sub={`${faulty} critical, ${onHold + data.blocked_riders.length} minor`}
          subTone={faulty > 0 ? "text-red-500" : "text-amber-600"}
          to="/batteries?status=Faulty" testid="kpi-fault-alerts" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SwapsChart byHour={data.swaps_by_hour} peakHour={data.peak_hour} />

        <div className="card-tactical p-5" data-testid="fault-alerts">
          <h3 className="font-heading font-bold text-[15px] m-0 mb-3">Fault alerts</h3>
          {data.fault_alerts.length === 0 ? (
            <p className="text-sm text-slate-400">All clear — no active fault alerts.</p>
          ) : (
            <div className="space-y-2">
              {data.fault_alerts.map((a, i) => (
                <div key={i} data-testid={`fault-item-${i}`}
                  className={`rounded-xl px-3.5 py-2.5 ${a.severity === "critical" ? "bg-rose-50" : "bg-sky-50/70"}`}>
                  <div className="flex items-start gap-2">
                    <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${a.severity === "critical" ? "bg-rose-500" : "bg-amber-500"}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold m-0 truncate">{a.title}</p>
                      <p className="text-xs text-slate-500 m-0 mt-0.5 truncate">{a.sub}{a.ts ? ` · ${timeAgo(a.ts)}` : ""}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-tactical p-5 lg:col-span-2" data-testid="live-swaps">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-[15px] m-0">Live swaps</h3>
            <span className="flex items-center gap-1.5 text-xs text-slate-500">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Streaming
            </span>
          </div>
          {data.live_swaps.length === 0 ? (
            <p className="text-sm text-slate-400">No swaps yet. Completed swaps stream in here.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.live_swaps.map((s) => (
                <div key={s.swap_id} className="py-3 flex items-center gap-3" data-testid={`live-swap-${s.swap_id}`}>
                  <span className="text-[11px] font-bold font-mono text-teal-700 bg-teal-50 border border-teal-100 rounded-lg px-2 py-1 shrink-0">
                    {s.swap_id.replace("SWP-", "S")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold m-0 truncate">{s.rider_name} swapped</p>
                    <p className="text-xs text-slate-500 m-0 mt-0.5 truncate font-mono">Batt {s.old} out · Batt {s.new} in</p>
                  </div>
                  <span className="text-xs text-slate-400 shrink-0">{timeAgo(s.ts)}</span>
                  <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1 shrink-0">Complete</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card-tactical p-5" data-testid="zone-status">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-[15px] m-0">Zone status</h3>
          </div>
          {data.zones.length === 0 ? (
            <p className="text-sm text-slate-400">No active riders yet. Onboard riders to see zone distribution.</p>
          ) : (
            <div className="space-y-3.5">
              {data.zones.map((z) => {
                const pct = totalRidersZones ? Math.round((z.count / totalRidersZones) * 100) : 0;
                return (
                  <div key={z.area} data-testid={`zone-row-${z.area}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{z.area}</span>
                      <span className={`font-mono text-xs ${pct < 20 ? "text-rose-500" : "text-slate-500"}`}>{pct}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${pct < 20 ? "bg-rose-400" : "bg-gradient-to-r from-teal-500 to-sky-500"}`}
                        style={{ width: `${Math.max(pct, 3)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="border-t border-slate-100 mt-4 pt-3 flex justify-between items-center">
            <span className="text-xs text-slate-500">Outstanding dues</span>
            <span className={`font-mono text-sm font-semibold ${k.outstanding_dues > 0 ? "text-red-500" : "text-emerald-600"}`}
              data-testid="outstanding-dues">{inr(k.outstanding_dues)}</span>
          </div>
        </div>
      </div>

    </div>
  );
}
